export type ButtonType = "button" | "submit" | "reset";

export type BcIconName =
  | "sort-by-name-asc"
  | "sort-by-name-desc"
  | "sort-by-value-asc"
  | "sort-by-value-desc"
  | "apply-filter"
  | "sort-by-sort-id";

/**
 * Defines the design version
 */
export type DesignVersion = "6" | "7" | "7new";

export type ControlsDesignVersion = "6" | "7";

export type ExtendedDesignVersion = "6" | "7" | "dark" | "light" | "mouseMode";

export type DropdownType = "dropdown" | "select";

export type TransitionDimension = "height" | "width";

export type TransitionEasing = "ease" | "linear" | "ease-in" | "ease-out" | "ease-in-out";

export type ControlSize = "sm" | "md" | "lg";

export type Placement =
  | "left-start"
  | "left"
  | "left-end"
  | "right-start"
  | "right"
  | "right-end"
  | "top-start"
  | "top"
  | "top-end"
  | "bottom-start"
  | "bottom"
  | "bottom-end";

export type PositioningStrategy = "absolute" | "fixed";

export type Point = {
  x: number;
  y: number;
};

export type InputType =
  | "text"
  | "number"
  | "email"
  | "password"
  | "search"
  | "url"
  | "tel"
  | "date"
  | "time"
  | "range"
  | "color";

export type PositionAndPlacement = {
  position: Point;
  placement: Placement;
};

export type OptionItems<T> = T[] | Func<T[] | PromiseLike<T[]>> | PromiseLike<T[]>;

export async function resolveItems<T>(items: OptionItems<T>): Promise<T[]> {
  let resolved: T[] | undefined = undefined;
  switch (typeof items) {
    case "function": {
      const itemsResult = items();
      if (isPromiseLike(itemsResult)) {
        resolved = await itemsResult;
      } else {
        resolved = itemsResult;
      }
      break;
    }
    case "object": {
      if (Array.isArray(items)) {
        resolved = items;
      } else if (isPromiseLike(items)) {
        resolved = await items;
      }
    }
  }
  if (!resolved) {
    throw new Error("The items provided is not valid");
  } else {
    return resolved;
  }
}

export function isPromiseLike<T>(obj: unknown): obj is PromiseLike<T> {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "then" in obj &&
    typeof obj.then === "function"
  );
}

export type Func<T> = () => T;

export type ModalPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "middle-left"
  | "middle-center"
  | "middle-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

export type CheckboxCheckmark = "square" | "tick" | "tick-outline";

export type RadioCheckmark = "circle" | "circle-outline";

export type BgImage =
  | "dm6-weather0-colored"
  | "dm6-weather0"
  | "dm6-weather1"
  | "dm6-weather2"
  | "dm6-weather3"
  | "dm6-weather-1"
  | "dm6-weather-2"
  | "dm6-weather-3"
  | "dm7-blau"
  | "dm7-rot"
  | "dm7-orange"
  | "fill";

export type ComponentPosition = "bottom" | "top";

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
